import { Cell } from '@/components/ui/shared/Table/Cell';
import { HeaderCell } from '@/components/ui/shared/Table/HeaderCell';
import { TableProfileCell } from '@/components/ui/shared/Table/presetCells/TableProfileCell';
import { TableDateCell } from '@/components/ui/shared/Table/presetCells/TableDateCell';
import { TableCountCell } from '@/components/ui/shared/Table/presetCells/TableCountCell';
import { Row } from '@/components/ui/shared/Table/Row';
import css from '@/components/ui/shared/Table/Table.module.css';
import { TableBody } from '@/components/ui/shared/Table/TableBody';
import { TableContext } from '@/components/ui/shared/Table/TableContext';
import { TableHead } from '@/components/ui/shared/Table/TableHead';
import { TablePaginationBar } from '@/components/ui/shared/Table/TablePaginationBar';
import type { TableProps } from '@/components/ui/shared/Table/types';

const EMPTY_SET: ReadonlySet<string> = new Set();

function TableRoot({
  sort,
  onSortChange,
  selectable = false,
  selectedIds = EMPTY_SET,
  onSelectionChange,
  rowIds = [],
  expandable = false,
  pagination,
  className,
  children
}: TableProps) {
  return (
    <div className={className}>
      <TableContext.Provider
        value={{
          sort,
          onSortChange,
          selectable,
          selectedIds,
          onSelectionChange,
          rowIds,
          expandable
        }}
      >
        <div role="table" className={css.table}>
          {children}
        </div>
      </TableContext.Provider>
      {pagination && (
        <TablePaginationBar
          totalRecords={pagination.totalRecords}
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          url={pagination.url}
          onChange={pagination.onChange}
          backgroundColor={pagination.backgroundColor}
        />
      )}
    </div>
  );
}

type TableComponent = typeof TableRoot & {
  Head: typeof TableHead;
  Body: typeof TableBody;
  HeaderCell: typeof HeaderCell;
  Row: typeof Row;
  Cell: typeof Cell;
  ProfileCell: typeof TableProfileCell;
  DateCell: typeof TableDateCell;
  CountCell: typeof TableCountCell;
};

/**
 * Compositional table: Table owns cross-cutting state (sorting, selection,
 * pagination, and whether a checkbox/chevron column is reserved) via
 * context, while consumers write the actual header/row/cell markup as JSX
 * children — see @/components/ui/shared/Table/index.ts for usage.
 */
export const Table = Object.assign(TableRoot, {
  Head: TableHead,
  Body: TableBody,
  HeaderCell,
  Row,
  Cell,
  ProfileCell: TableProfileCell,
  DateCell: TableDateCell,
  CountCell: TableCountCell
}) as TableComponent;
